import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Boat, GasPump, Anchor, Package, Compass, Sailboat, Wrench, Crane, GlobeHemisphereWest, ArrowUpRight,
} from "@phosphor-icons/react";
import CTABanner from "../components/sections/CTABanner";
import useSEO from "../lib/useSEO";

const ICONS = { Boat, GasPump, Anchor, Package, Compass, Sailboat, Wrench, Crane, GlobeHemisphereWest };

const SERVICES = [
    {
        slug: "vessel-chartering", title: "Vessel Chartering", icon: "Boat",
        summary: "Spot, time and bareboat charters across dry bulk, project cargo and specialized tonnage.",
        highlights: ["Voyage estimation", "Fixture management", "Post-fixture reporting", "Demurrage control"],
    },
    {
        slug: "tanker-chartering", title: "Tanker Chartering", icon: "GasPump",
        summary: "Crude, product, chemical and gas tanker chartering with global trading desks.",
        highlights: ["Crude / Product / Chem", "Vetting + SIRE", "TC trading", "STS support"],
    },
    {
        slug: "offshore-marine-support", title: "Offshore Marine Support", icon: "Anchor",
        summary: "AHTS, PSV, crew boats and offshore logistics for upstream operators worldwide.",
        highlights: ["AHTS / PSV / FSV", "Rig moves", "Subsea support", "Crew transfer"],
    },
    {
        slug: "cargo-transportation", title: "Cargo Transportation", icon: "Package",
        summary: "Door-to-door multimodal cargo transportation at industrial scale.",
        highlights: ["Multimodal", "Project cargo", "Heavy lift", "Reefer + ambient"],
    },
    {
        slug: "marine-logistics", title: "Marine Logistics", icon: "Compass",
        summary: "End-to-end marine logistics, lashing, stowage and transit planning.",
        highlights: ["Stowage planning", "Lashing", "Transit risk", "Insurance liaison"],
    },
    {
        slug: "shipbuilding", title: "Shipbuilding", icon: "Sailboat",
        summary: "New-build management, shipyard supervision and class certification across Asian yards.",
        highlights: ["New-build supervision", "Yard sourcing", "Class certification", "Delivery audit"],
    },
    {
        slug: "ship-repairs", title: "Ship Repairs & Maintenance", icon: "Wrench",
        summary: "Drydock coordination, scheduled maintenance and emergency repairs worldwide.",
        highlights: ["Drydock mgmt", "Scheduled maintenance", "Voyage repairs", "Emergency response"],
    },
    {
        slug: "port-operations", title: "Port Operations", icon: "Crane",
        summary: "Stevedoring, husbandry, bunkering and 24/7 port agency services.",
        highlights: ["Husbandry", "Bunkering", "Stevedoring", "Agency mgmt"],
    },
    {
        slug: "freight-coordination", title: "Freight Coordination", icon: "GlobeHemisphereWest",
        summary: "Freight brokerage, voyage estimation and post-fixture management.",
        highlights: ["Brokerage", "Voyage estimation", "Documentation", "Post-fixture"],
    },
];

export default function Services() {
    useSEO({
        title: "Services — Windermere Shipping",
        description: "Nine integrated maritime service desks: chartering, tankers, offshore, cargo, logistics, shipbuilding, repairs, port ops and freight.",
    });

    return (
        <>
            <section className="relative pt-36 md:pt-44 pb-20 md:pb-28 bg-[#040914] border-b border-white/5 overflow-hidden">
                <div className="absolute inset-0 wms-radial pointer-events-none" />
                <div className="wms-container relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-7">
                        <span className="wms-overline">Services — Capabilities</span>
                        <h1 className="mt-6 font-display font-light text-[clamp(2.4rem,6vw,5rem)] leading-[1.05] tracking-tight">
                            Nine desks. <br /> <span className="italic text-[#48CAE4]">One operations spine.</span>
                        </h1>
                    </div>
                    <p className="lg:col-span-5 text-white/60 text-base md:text-lg leading-relaxed">
                        Each Windermere service desk is staffed by senior practitioners and supported by a unified post-fixture, finance, HSEQ and ESG backbone.
                    </p>
                </div>
            </section>

            <section className="py-20 md:py-24 bg-[#040914]">
                <div className="wms-container space-y-px">
                    {SERVICES.map((s, i) => {
                        const Icon = ICONS[s.icon] || Boat;
                        const reversed = i % 2 === 1;
                        return (
                            <motion.article
                                id={s.slug}
                                key={s.slug}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-100px" }}
                                transition={{ duration: 0.5 }}
                                className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-white/5 border border-white/5"
                                data-testid={`service-section-${s.slug}`}
                            >
                                <div className={`lg:col-span-5 bg-[#0B1324] p-8 md:p-12 flex flex-col justify-between ${reversed ? "lg:order-2" : ""}`}>
                                    <div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs tracking-[0.32em] uppercase text-white/40">{String(i + 1).padStart(2, "0")} / {String(SERVICES.length).padStart(2, "0")}</span>
                                            <Icon size={32} weight="duotone" className="text-[#48CAE4]" />
                                        </div>
                                        <h2 className="mt-8 font-display font-light text-3xl md:text-4xl tracking-tight">{s.title}</h2>
                                        <p className="mt-5 text-white/65 leading-relaxed">{s.summary}</p>
                                    </div>
                                    <Link
                                        to={`/contact?intent=${s.slug}`}
                                        data-testid={`service-cta-${s.slug}`}
                                        className="mt-10 inline-flex items-center gap-2 px-6 py-3 bg-white text-[#040914] text-xs uppercase tracking-[0.24em] hover:bg-[#48CAE4] transition-colors w-max"
                                    >
                                        Speak to Desk <ArrowUpRight size={14} weight="bold" />
                                    </Link>
                                </div>
                                <div className={`lg:col-span-7 bg-[#040914] p-8 md:p-12 ${reversed ? "lg:order-1" : ""}`}>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        {s.highlights.map((h, k) => (
                                            <div key={h} className="flex items-start gap-4 border-l border-white/10 pl-4 py-2">
                                                <span className="font-display text-xs text-[#48CAE4] tracking-[0.32em]">{String(k + 1).padStart(2, "0")}</span>
                                                <span className="text-sm text-white/80">{h}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="mt-10 pt-6 border-t border-white/10 grid grid-cols-3 gap-6 text-center">
                                        {[
                                            { l: "Avg Response", v: "<24h" },
                                            { l: "Geo Coverage", v: "Global" },
                                            { l: "Active Clients", v: "120+" },
                                        ].map((m) => (
                                            <div key={m.l}>
                                                <div className="font-display text-2xl text-white">{m.v}</div>
                                                <div className="mt-1 text-[10px] tracking-[0.28em] uppercase text-white/40">{m.l}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </motion.article>
                        );
                    })}
                </div>
            </section>

            <CTABanner />
        </>
    );
}
